import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  AppBar, Avatar, Badge, Box, IconButton,
  Menu, MenuItem, Toolbar, Tooltip, Typography, Divider
} from '@mui/material';
import {
  Menu as MenuIcon, Notifications as NotificationsIcon,
  AccountCircle, Logout, Chat as ChatIcon
} from '@mui/icons-material';
import { logout } from '../../redux/slices/authSlice';
import API from '../../utils/api';
import { getCachedGym } from '../../utils/cachedData';
import { useBadges, setNotificationCount } from '../../utils/badgeStore';
import { format } from 'date-fns';
import LanguageSwitcher from '../LanguageSwitcher';

const Navbar = ({ open, toggleDrawer }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [gymInfo, setGymInfo] = useState(null);

  // Notification + chat badges come from a single shared poller (badgeStore),
  // so Navbar and DynamicSidebar no longer each fire their own polls.
  const { unreadChatCount, notificationCount, notifications } = useBadges();
  const recentNotifications = notifications.slice(0, 10);
  
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handleNotificationsMenuOpen = async (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    
    // Mark all notifications as read when opening the menu
    try {
      // Call API to mark notifications as read
      await API.patch('/notifications/mark-all-read');
      // Reset the shared badge count to 0 (no extra fetch needed)
      setNotificationCount(0);
    } catch (error) {
      console.error('Error marking notifications as read:', error);
    }
  };
  
  const handleMenuClose = () => {
    setAnchorEl(null);
    setNotificationsAnchorEl(null);
  };
  
  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };
  
  const handleLogout = () => {
    handleMenuClose();
    dispatch(logout());
    navigate('/login');
  };

  // Gym info (name + logo) is read on nearly every page — serve it from the
  // shared cache so navigating between pages doesn't refetch it.
  useEffect(() => {
    let cancelled = false;
    const fetchGymInfo = async () => {
      try {
        if (user?.gymId) {
          const gymData = await getCachedGym(user.gymId);
          if (!cancelled) setGymInfo(gymData);
        }
      } catch (error) {
        console.error('Navbar: Error fetching gym info:', error);
      }
    };
    fetchGymInfo();
    return () => { cancelled = true; };
  }, [user]);

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        zIndex: (theme) => theme.zIndex.drawer + 1,
        transition: 'width 0.3s',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={toggleDrawer}
          sx={{ mr: 2 }}
        >
          <MenuIcon />
        </IconButton>
        
        <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
          {gymInfo?.logo && (
            <Avatar
              src={gymInfo.logo}
              alt={gymInfo.gymName}
              sx={{ width: 40, height: 40, mr: 2 }}
              onError={(e) => {
                console.log('🏢 Logo failed to load:', gymInfo.logo?.substring(0, 100));
                e.target.style.display = 'none';
              }}
            />
          )}
          <Typography
            variant="h6"
            noWrap
            component="div"
          >
            {gymInfo?.gymName || 'Gym Management System'}
          </Typography>
        </Box>
        
        {/* Notifications + Chat Badge */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <LanguageSwitcher />

          {/* Chat unread badge */}
          <Tooltip title="Messages">
            <IconButton color="inherit" onClick={() => navigate('/chat')} sx={{ mr: 0.5 }}>
              <Badge badgeContent={unreadChatCount} color="error" max={99}>
                <ChatIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          
          <IconButton
            color="inherit"
            onClick={handleNotificationsMenuOpen}
          >
            <Badge badgeContent={notificationCount} color="error">
              <NotificationsIcon />
            </Badge>
          </IconButton>
          
          <Menu
            anchorEl={notificationsAnchorEl}
            open={Boolean(notificationsAnchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: 320, maxHeight: 400, mt: 1.5 }
            }}
          >
            {recentNotifications.length === 0 ? (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  No new notifications
                </Typography>
              </MenuItem>
            ) : (
              recentNotifications.map((notification, index) => {
                const isAnnouncement = notification.title?.startsWith('[Announcement]');
                const isSubWarning = notification.title?.toLowerCase().includes('subscription');
                const accentColor = isAnnouncement ? '#6366f1' : isSubWarning ? '#f59e0b' : null;
                return (
                <Box key={notification.id || index}>
                  <MenuItem onClick={handleMenuClose} sx={{ alignItems: 'flex-start', py: 1.5,
                    borderLeft: accentColor ? `3px solid ${accentColor}` : '3px solid transparent' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: notification.isRead ? 'normal' : 'bold',
                        color: accentColor || 'inherit', fontSize: 13 }}>
                        {notification.title?.replace('[Announcement] ', '') || notification.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12,
                        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        {format(new Date(notification.createdAt), 'MMM d, h:mm a')}
                      </Typography>
                    </Box>
                  </MenuItem>
                  {index < recentNotifications.length - 1 && <Divider />}
                </Box>
              )})
            )}
          </Menu>
          
          {/* Profile */}
          <Tooltip title="Account settings">
            <IconButton
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              {user?.name ? (
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.name.charAt(0).toUpperCase()}
                </Avatar>
              ) : (
                <AccountCircle />
              )}
            </IconButton>
          </Tooltip>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              sx: { width: 200, mt: 1.5 }
            }}
          >
            <MenuItem onClick={handleProfile}>
              <AccountCircle sx={{ mr: 2 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <Logout sx={{ mr: 2 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
